import { useMemo } from 'react';

import { UserLimit } from '@/feature/responsible-gaming/hooks/types';
import { getWagerLimitErrorMessage } from '@/utils/errors/entry-errors';

export const useRaceToPlaceAmountValidation = ({
    amount,
    cash,
    minEntryAmount,
    maxEntryAmount,
    userLimits = [],
}: {
    amount: number;
    cash: number;
    minEntryAmount: number;
    maxEntryAmount: number;
    userLimits: UserLimit[];
}) => {
    return useMemo(() => {
        const limits = [
            { limit: cash, message: `Entry amount exceeds balance of $${cash.toFixed(2)}` },
            {
                limit: maxEntryAmount,
                message: `You exceeded the current max entry ($${maxEntryAmount.toFixed(2)})`,
            },
            {
                limit: maxEntryAmount,
                message: `Minimum entry amount is $${minEntryAmount.toFixed(2)}`,
                minLimit: minEntryAmount,
            },
        ];

        userLimits.forEach(limit => {
            const remainingAmount = Math.max(0, limit.amount - limit.current_state);
            limits.push({ limit: remainingAmount, message: getWagerLimitErrorMessage(amount.toString(), userLimits) });
        });

        const sortedLimits = limits.sort((a, b) => a.limit - b.limit);
        const breachedLimit =
            amount > 0 ? sortedLimits.find(l => amount > l.limit || (l.minLimit && amount < l.minLimit)) : undefined;

        const limitsWithoutBalanceMax = sortedLimits.filter(it => it.limit !== cash);
        const maxAllowed =
            limitsWithoutBalanceMax.length > 0
                ? Math.min(...limitsWithoutBalanceMax.map(it => it.limit))
                : maxEntryAmount;

        return {
            maxAmount: maxAllowed,
            errorMessage: breachedLimit?.message,
        };
    }, [amount, cash, minEntryAmount, maxEntryAmount, userLimits]);
};
